'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  Calculator,
  Plus,
  Edit2,
  Trash2,
  Save,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Copy,
  ToggleLeft,
  ToggleRight,
  Info,
  Search,
  Filter,
  FlaskConical,
  BookOpen,
  Layers,
  MapPin,
  Home,
  Building2,
  Warehouse,
  TreePine,
  X,
} from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type PropertyType = 'Residential' | 'Commercial' | 'Industrial' | 'Vacant Land' | 'Farmland' | 'Public-Land' | 'Mixed Use';
type LocationZone = 'Zone A – Central Monrovia' | 'Zone B – Greater Monrovia' | 'Zone C – Secondary Cities' | 'Zone D – Rural Towns' | 'Zone E – Remote/Rural';
type SizeBand = 'Micro (<100 m²)' | 'Small (100–500 m²)' | 'Medium (500–2,000 m²)' | 'Large (2,000–10,000 m²)' | 'Extra-Large (>10,000 m²)';
type UseCase = 'Owner-Occupied' | 'Rental / Leased' | 'Commercial Operations' | 'Agricultural' | 'Institutional / NGO' | 'Government' | 'Vacant / Idle';
type FormulaStatus = 'active' | 'draft' | 'archived';

interface ValuationFormula {
  id: string;
  name: string;
  propertyType: PropertyType;
  locationZone: LocationZone;
  sizeBand: SizeBand;
  useCase: UseCase;
  baseRatePerSqm: number;
  locationMultiplier: number;
  useMultiplier: number;
  depreciationRate: number;
  minTax: number;
  maxTax: number;
  notes: string;
  status: FormulaStatus;
  lastUpdated: string;
  updatedBy: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const INITIAL_FORMULAS: ValuationFormula[] = [
  {
    id: 'VF-001',
    name: 'Central Monrovia Commercial',
    propertyType: 'Commercial',
    locationZone: 'Zone A – Central Monrovia',
    sizeBand: 'Medium (500–2,000 m²)',
    useCase: 'Commercial Operations',
    baseRatePerSqm: 4.80,
    locationMultiplier: 1.85,
    useMultiplier: 1.40,
    depreciationRate: 2.0,
    minTax: 1200,
    maxTax: 85000,
    notes: 'Applies to all commercial properties in CBD. Reviewed annually per LRA Circular 2025-04.',
    status: 'active',
    lastUpdated: '2026-06-15',
    updatedBy: 'J. Jallah',
  },
  {
    id: 'VF-002',
    name: 'Greater Monrovia Residential',
    propertyType: 'Residential',
    locationZone: 'Zone B – Greater Monrovia',
    sizeBand: 'Small (100–500 m²)',
    useCase: 'Owner-Occupied',
    baseRatePerSqm: 1.20,
    locationMultiplier: 1.30,
    useMultiplier: 0.85,
    depreciationRate: 3.5,
    minTax: 150,
    maxTax: 12000,
    notes: 'Standard residential owner-occupied rate. 15% relief applied for primary residence.',
    status: 'active',
    lastUpdated: '2026-05-20',
    updatedBy: 'M. Kollie',
  },
  {
    id: 'VF-003',
    name: 'Industrial Margibi Corridor',
    propertyType: 'Industrial',
    locationZone: 'Zone C – Secondary Cities',
    sizeBand: 'Large (2,000–10,000 m²)',
    useCase: 'Commercial Operations',
    baseRatePerSqm: 2.60,
    locationMultiplier: 1.10,
    useMultiplier: 1.55,
    depreciationRate: 4.0,
    minTax: 3500,
    maxTax: 200000,
    notes: 'Covers SEZ and industrial park properties along the Monrovia–Ganta corridor.',
    status: 'active',
    lastUpdated: '2026-04-10',
    updatedBy: 'J. Jallah',
  },
  {
    id: 'VF-004',
    name: 'Rural Farmland Standard',
    propertyType: 'Farmland',
    locationZone: 'Zone E – Remote/Rural',
    sizeBand: 'Extra-Large (>10,000 m²)',
    useCase: 'Agricultural',
    baseRatePerSqm: 0.08,
    locationMultiplier: 0.60,
    useMultiplier: 0.70,
    depreciationRate: 1.0,
    minTax: 50,
    maxTax: 8000,
    notes: 'Concessional rate for subsistence and commercial farming. Exempt if < 2 ha.',
    status: 'active',
    lastUpdated: '2026-03-01',
    updatedBy: 'T. Freeman',
  },
  {
    id: 'VF-005',
    name: 'Vacant Land Urban Penalty',
    propertyType: 'Vacant Land',
    locationZone: 'Zone A – Central Monrovia',
    sizeBand: 'Small (100–500 m²)',
    useCase: 'Vacant / Idle',
    baseRatePerSqm: 3.20,
    locationMultiplier: 2.00,
    useMultiplier: 1.80,
    depreciationRate: 0.0,
    minTax: 800,
    maxTax: 45000,
    notes: 'Penalty surcharge for idle urban land. Encourages development. IMF-aligned anti-speculation measure.',
    status: 'active',
    lastUpdated: '2026-06-01',
    updatedBy: 'J. Jallah',
  },
  {
    id: 'VF-006',
    name: 'Mixed-Use Rental Draft',
    propertyType: 'Mixed Use',
    locationZone: 'Zone B – Greater Monrovia',
    sizeBand: 'Medium (500–2,000 m²)',
    useCase: 'Rental / Leased',
    baseRatePerSqm: 2.10,
    locationMultiplier: 1.45,
    useMultiplier: 1.20,
    depreciationRate: 2.5,
    minTax: 600,
    maxTax: 30000,
    notes: 'Draft formula pending LRA Board approval. Not yet applied in Stage 5.',
    status: 'draft',
    lastUpdated: '2026-07-01',
    updatedBy: 'M. Kollie',
  },
];

const PROPERTY_TYPES: PropertyType[] = ['Residential', 'Commercial', 'Industrial', 'Vacant Land', 'Farmland', 'Public-Land', 'Mixed Use'];
const LOCATION_ZONES: LocationZone[] = ['Zone A – Central Monrovia', 'Zone B – Greater Monrovia', 'Zone C – Secondary Cities', 'Zone D – Rural Towns', 'Zone E – Remote/Rural'];
const SIZE_BANDS: SizeBand[] = ['Micro (<100 m²)', 'Small (100–500 m²)', 'Medium (500–2,000 m²)', 'Large (2,000–10,000 m²)', 'Extra-Large (>10,000 m²)'];
const USE_CASES: UseCase[] = ['Owner-Occupied', 'Rental / Leased', 'Commercial Operations', 'Agricultural', 'Institutional / NGO', 'Government', 'Vacant / Idle'];

const propertyTypeIcon: Record<PropertyType, React.ReactNode> = {
  'Residential': <Home size={14} />,
  'Commercial': <Building2 size={14} />,
  'Industrial': <Warehouse size={14} />,
  'Vacant Land': <MapPin size={14} />,
  'Farmland': <TreePine size={14} />,
  'Public-Land': <Layers size={14} />,
  'Mixed Use': <Layers size={14} />,
};

const statusConfig: Record<FormulaStatus, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  draft: { label: 'Draft', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
  archived: { label: 'Archived', color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
};

// ─── Formula Calculator Preview ───────────────────────────────────────────────

function calcEstimatedTax(f: ValuationFormula, areaSqm: number): number {
  const gross = areaSqm * f.baseRatePerSqm * f.locationMultiplier * f.useMultiplier;
  const depreciated = gross * (1 - f.depreciationRate / 100);
  return Math.min(Math.max(depreciated, f.minTax), f.maxTax);
}

// ─── Empty Formula ─────────────────────────────────────────────────────────────

function emptyFormula(): Omit<ValuationFormula, 'id' | 'lastUpdated' | 'updatedBy'> {
  return {
    name: '',
    propertyType: 'Residential',
    locationZone: 'Zone A – Central Monrovia',
    sizeBand: 'Small (100–500 m²)',
    useCase: 'Owner-Occupied',
    baseRatePerSqm: 1.00,
    locationMultiplier: 1.00,
    useMultiplier: 1.00,
    depreciationRate: 0.0,
    minTax: 100,
    maxTax: 50000,
    notes: '',
    status: 'draft',
  };
}

// ─── Formula Modal ─────────────────────────────────────────────────────────────

interface FormulaModalProps {
  formula: ValuationFormula | null;
  onClose: () => void;
  onSave: (f: ValuationFormula) => void;
}

function FormulaModal({ formula, onClose, onSave }: FormulaModalProps) {
  const isNew = !formula?.id || formula.id === '__new__';
  const [form, setForm] = useState<Omit<ValuationFormula, 'id' | 'lastUpdated' | 'updatedBy'>>(
    formula && formula.id !== '__new__'
      ? {
          name: formula.name,
          propertyType: formula.propertyType,
          locationZone: formula.locationZone,
          sizeBand: formula.sizeBand,
          useCase: formula.useCase,
          baseRatePerSqm: formula.baseRatePerSqm,
          locationMultiplier: formula.locationMultiplier,
          useMultiplier: formula.useMultiplier,
          depreciationRate: formula.depreciationRate,
          minTax: formula.minTax,
          maxTax: formula.maxTax,
          notes: formula.notes,
          status: formula.status,
        }
      : emptyFormula()
  );
  const [previewArea, setPreviewArea] = useState(500);

  const previewTax = calcEstimatedTax({ ...form, id: '', lastUpdated: '', updatedBy: '' }, previewArea);

  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave({
      ...form,
      id: formula?.id && formula.id !== '__new__' ? formula.id : `VF-${Date.now()}`,
      lastUpdated: new Date().toISOString().split('T')[0],
      updatedBy: 'J. Jallah',
    });
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: 'var(--dark-elevated)',
    border: '1px solid var(--dark-border)',
    color: 'var(--dark-text)',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    width: '100%',
    outline: 'none',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: 'var(--dark-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: '4px',
    display: 'block',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-2xl"
        style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(212,160,23,0.15)' }}>
              <Calculator size={16} style={{ color: 'var(--accent)' }} />
            </div>
            <div>
              <h2 className="font-bold text-sm" style={{ color: 'var(--dark-text)' }}>
                {isNew ? 'New Valuation Formula' : 'Edit Formula'}
              </h2>
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Stage 4 — Property Assessment (Valuation)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-white/5 transition-colors">
            <X size={16} style={{ color: 'var(--dark-muted)' }} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Name + Status */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label style={labelStyle}>Formula Name *</label>
              <input
                style={inputStyle}
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Central Monrovia Commercial"
              />
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inputStyle} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value as FormulaStatus }))}>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Dimensions */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label style={labelStyle}>Property Type</label>
              <select style={inputStyle} value={form.propertyType} onChange={e => setForm(p => ({ ...p, propertyType: e.target.value as PropertyType }))}>
                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Location Zone</label>
              <select style={inputStyle} value={form.locationZone} onChange={e => setForm(p => ({ ...p, locationZone: e.target.value as LocationZone }))}>
                {LOCATION_ZONES.map(z => <option key={z} value={z}>{z}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Size Band</label>
              <select style={inputStyle} value={form.sizeBand} onChange={e => setForm(p => ({ ...p, sizeBand: e.target.value as SizeBand }))}>
                {SIZE_BANDS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Use Case</label>
              <select style={inputStyle} value={form.useCase} onChange={e => setForm(p => ({ ...p, useCase: e.target.value as UseCase }))}>
                {USE_CASES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>

          {/* Formula Parameters */}
          <div
            className="rounded-lg p-4 space-y-4"
            style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}
          >
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--accent)' }}>
              Formula Parameters
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label style={labelStyle}>Base Rate (USD/m²)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  style={inputStyle}
                  value={form.baseRatePerSqm}
                  onChange={e => setForm(p => ({ ...p, baseRatePerSqm: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <label style={labelStyle}>Location Multiplier (×)</label>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  style={inputStyle}
                  value={form.locationMultiplier}
                  onChange={e => setForm(p => ({ ...p, locationMultiplier: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <label style={labelStyle}>Use-Case Multiplier (×)</label>
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  style={inputStyle}
                  value={form.useMultiplier}
                  onChange={e => setForm(p => ({ ...p, useMultiplier: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <label style={labelStyle}>Depreciation Rate (%)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="50"
                  style={inputStyle}
                  value={form.depreciationRate}
                  onChange={e => setForm(p => ({ ...p, depreciationRate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <label style={labelStyle}>Min Tax (USD)</label>
                <input
                  type="number"
                  min="0"
                  style={inputStyle}
                  value={form.minTax}
                  onChange={e => setForm(p => ({ ...p, minTax: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <label style={labelStyle}>Max Tax Cap (USD)</label>
                <input
                  type="number"
                  min="0"
                  style={inputStyle}
                  value={form.maxTax}
                  onChange={e => setForm(p => ({ ...p, maxTax: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            </div>

            {/* Formula Display */}
            <div
              className="rounded-md px-4 py-3 font-mono text-xs"
              style={{ backgroundColor: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.2)', color: 'var(--dark-text)' }}
            >
              <span style={{ color: 'var(--dark-muted)' }}>Tax = </span>
              <span style={{ color: 'var(--accent)' }}>CLAMP(</span>
              Area × {form.baseRatePerSqm.toFixed(2)} × {form.locationMultiplier.toFixed(2)} × {form.useMultiplier.toFixed(2)} × (1 − {form.depreciationRate.toFixed(1)}%),
              <span style={{ color: '#34d399' }}> min={form.minTax}</span>,
              <span style={{ color: '#f87171' }}> max={form.maxTax}</span>
              <span style={{ color: 'var(--accent)' }}>)</span>
            </div>
          </div>

          {/* Live Preview */}
          <div
            className="rounded-lg p-4"
            style={{ backgroundColor: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.2)' }}
          >
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-bold uppercase tracking-widest" style={{ color: '#34d399' }}>
                <FlaskConical size={12} className="inline mr-1" />
                Live Preview
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Test area (m²):</span>
                <input
                  type="number"
                  min="1"
                  style={{ ...inputStyle, width: '80px' }}
                  value={previewArea}
                  onChange={e => setPreviewArea(parseInt(e.target.value) || 1)}
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Estimated Annual Tax</p>
                <p className="text-2xl font-bold" style={{ color: '#34d399' }}>
                  ${previewTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Effective Rate / m²</p>
                <p className="text-lg font-semibold" style={{ color: 'var(--dark-text)' }}>
                  ${(previewTax / previewArea).toFixed(3)}
                </p>
              </div>
              <div>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Gross (pre-depreciation)</p>
                <p className="text-lg font-semibold" style={{ color: 'var(--dark-muted)' }}>
                  ${(previewArea * form.baseRatePerSqm * form.locationMultiplier * form.useMultiplier).toFixed(2)}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={labelStyle}>Admin Notes / Policy Reference</label>
            <textarea
              rows={3}
              style={{ ...inputStyle, resize: 'vertical' }}
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="LRA circular reference, exemption conditions, review schedule..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
          <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
            {form.status === 'active' ?'✓ Active formulas are auto-applied in Stage 5 Assessment' :'⚠ Draft formulas are not applied until set to Active'}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/5"
              style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!form.name.trim()}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{
                backgroundColor: form.name.trim() ? 'var(--accent)' : 'rgba(212,160,23,0.3)',
                color: form.name.trim() ? 'var(--accent-foreground)' : 'var(--dark-muted)',
                cursor: form.name.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              <Save size={14} />
              {isNew ? 'Create Formula' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Formula Card ──────────────────────────────────────────────────────────────

interface FormulaCardProps {
  formula: ValuationFormula;
  onEdit: () => void;
  onDuplicate: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

function FormulaCard({ formula, onEdit, onDuplicate, onToggleStatus, onDelete }: FormulaCardProps) {
  const [expanded, setExpanded] = useState(false);
  const sc = statusConfig[formula.status];
  const previewTax = calcEstimatedTax(formula, 500);

  return (
    <div
      className="rounded-xl transition-all duration-200"
      style={{
        backgroundColor: 'var(--dark-surface)',
        border: `1px solid ${formula.status === 'active' ? 'rgba(212,160,23,0.25)' : 'var(--dark-border)'}`,
      }}
    >
      {/* Card Header */}
      <div className="flex items-start justify-between p-4 gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{ backgroundColor: 'rgba(212,160,23,0.12)' }}
          >
            <span style={{ color: 'var(--accent)' }}>{propertyTypeIcon[formula.propertyType]}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-sm truncate" style={{ color: 'var(--dark-text)' }}>
                {formula.name}
              </h3>
              <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: sc.bg, color: sc.color }}
              >
                {sc.label}
              </span>
              <span
                className="text-xs px-1.5 py-0.5 rounded flex-shrink-0"
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: 'var(--dark-muted)' }}
              >
                {formula.id}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                {formula.propertyType} · {formula.locationZone.split('–')[0].trim()}
              </span>
              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                {formula.sizeBand.split('(')[0].trim()} · {formula.useCase}
              </span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Est. Tax @ 500m²</p>
            <p className="text-sm font-bold" style={{ color: 'var(--accent)' }}>
              ${previewTax.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </p>
          </div>
          <div className="text-right hidden md:block">
            <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>Base Rate</p>
            <p className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>
              ${formula.baseRatePerSqm}/m²
            </p>
          </div>
          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleStatus}
              className="p-1.5 rounded-md hover:bg-white/5 transition-colors"
              title={formula.status === 'active' ? 'Deactivate' : 'Activate'}
            >
              {formula.status === 'active'
                ? <ToggleRight size={16} style={{ color: '#34d399' }} />
                : <ToggleLeft size={16} style={{ color: 'var(--dark-muted)' }} />}
            </button>
            <button onClick={onEdit} className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Edit">
              <Edit2 size={14} style={{ color: 'var(--dark-muted)' }} />
            </button>
            <button onClick={onDuplicate} className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Duplicate">
              <Copy size={14} style={{ color: 'var(--dark-muted)' }} />
            </button>
            <button onClick={onDelete} className="p-1.5 rounded-md hover:bg-white/5 transition-colors" title="Delete">
              <Trash2 size={14} style={{ color: '#f87171' }} />
            </button>
            <button
              onClick={() => setExpanded(p => !p)}
              className="p-1.5 rounded-md hover:bg-white/5 transition-colors"
              title="Expand"
            >
              {expanded ? <ChevronUp size={14} style={{ color: 'var(--dark-muted)' }} /> : <ChevronDown size={14} style={{ color: 'var(--dark-muted)' }} />}
            </button>
          </div>
        </div>
      </div>

      {/* Expanded Detail */}
      {expanded && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--dark-border)' }}>
          <div className="pt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { label: 'Base Rate', value: `$${formula.baseRatePerSqm}/m²` },
              { label: 'Location ×', value: `×${formula.locationMultiplier}` },
              { label: 'Use-Case ×', value: `×${formula.useMultiplier}` },
              { label: 'Depreciation', value: `${formula.depreciationRate}%` },
              { label: 'Min Tax', value: `$${formula.minTax.toLocaleString()}` },
              { label: 'Max Cap', value: `$${formula.maxTax.toLocaleString()}` },
              { label: 'Last Updated', value: formula.lastUpdated },
              { label: 'Updated By', value: formula.updatedBy },
            ].map(item => (
              <div key={item.label} className="rounded-lg p-3" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{item.label}</p>
                <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--dark-text)' }}>{item.value}</p>
              </div>
            ))}
          </div>

          {/* Formula expression */}
          <div
            className="rounded-md px-4 py-2.5 font-mono text-xs mb-3"
            style={{ backgroundColor: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.15)', color: 'var(--dark-text)' }}
          >
            <span style={{ color: 'var(--dark-muted)' }}>Tax = </span>
            <span style={{ color: 'var(--accent)' }}>CLAMP(</span>
            Area × {formula.baseRatePerSqm} × {formula.locationMultiplier} × {formula.useMultiplier} × (1 − {formula.depreciationRate}%),
            <span style={{ color: '#34d399' }}> min={formula.minTax}</span>,
            <span style={{ color: '#f87171' }}> max={formula.maxTax}</span>
            <span style={{ color: 'var(--accent)' }}>)</span>
          </div>

          {formula.notes && (
            <div
              className="flex gap-2 rounded-md px-3 py-2.5"
              style={{ backgroundColor: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.15)' }}
            >
              <Info size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#60a5fa' }} />
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{formula.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function ValuationConfigClient() {
  const [formulas, setFormulas] = useState<ValuationFormula[]>(INITIAL_FORMULAS);
  const [modalFormula, setModalFormula] = useState<ValuationFormula | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<PropertyType | 'All'>('All');
  const [filterZone, setFilterZone] = useState<LocationZone | 'All'>('All');
  const [filterStatus, setFilterStatus] = useState<FormulaStatus | 'All'>('All');
  const [activeTab, setActiveTab] = useState<'formulas' | 'matrix' | 'guide'>('formulas');
  const [savedAlert, setSavedAlert] = useState(false);

  const filtered = formulas.filter(f => {
    const matchSearch = f.name.toLowerCase().includes(search.toLowerCase()) ||
      f.propertyType.toLowerCase().includes(search.toLowerCase()) ||
      f.useCase.toLowerCase().includes(search.toLowerCase());
    const matchType = filterType === 'All' || f.propertyType === filterType;
    const matchZone = filterZone === 'All' || f.locationZone === filterZone;
    const matchStatus = filterStatus === 'All' || f.status === filterStatus;
    return matchSearch && matchType && matchZone && matchStatus;
  });

  const stats = {
    total: formulas.length,
    active: formulas.filter(f => f.status === 'active').length,
    draft: formulas.filter(f => f.status === 'draft').length,
    archived: formulas.filter(f => f.status === 'archived').length,
  };

  const handleSave = (f: ValuationFormula) => {
    setFormulas(prev => {
      const idx = prev.findIndex(x => x.id === f.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = f;
        return next;
      }
      return [...prev, f];
    });
    setShowModal(false);
    setSavedAlert(true);
    setTimeout(() => setSavedAlert(false), 3000);
  };

  const handleDuplicate = (f: ValuationFormula) => {
    const dup: ValuationFormula = {
      ...f,
      id: `VF-${Date.now()}`,
      name: `${f.name} (Copy)`,
      status: 'draft',
      lastUpdated: new Date().toISOString().split('T')[0],
    };
    setFormulas(prev => [...prev, dup]);
  };

  const handleToggleStatus = (id: string) => {
    setFormulas(prev => prev.map(f => {
      if (f.id !== id) return f;
      const next: FormulaStatus = f.status === 'active' ? 'draft' : 'active';
      return { ...f, status: next, lastUpdated: new Date().toISOString().split('T')[0] };
    }));
  };

  const handleDelete = (id: string) => {
    setFormulas(prev => prev.filter(f => f.id !== id));
  };

  const openNew = () => {
    setModalFormula({ id: '__new__', lastUpdated: '', updatedBy: '', ...emptyFormula() });
    setShowModal(true);
  };

  const openEdit = (f: ValuationFormula) => {
    setModalFormula(f);
    setShowModal(true);
  };

  const selectStyle: React.CSSProperties = {
    backgroundColor: 'var(--dark-elevated)',
    border: '1px solid var(--dark-border)',
    color: 'var(--dark-text)',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '12px',
    outline: 'none',
  };

  // Matrix data: property types × zones
  const matrixData = PROPERTY_TYPES.map(pt => ({
    type: pt,
    zones: LOCATION_ZONES.map(zone => {
      const match = formulas.find(f => f.propertyType === pt && f.locationZone === zone && f.status === 'active');
      return { zone, formula: match || null };
    }),
  }));

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        {/* Page Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}
              >
                STAGE 4
              </span>
              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Property Assessment (Valuation)</span>
            </div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--dark-text)' }}>
              Valuation Formula Configuration
            </h1>
            <p className="text-sm mt-1" style={{ color: 'var(--dark-muted)' }}>
              Define and manage tax valuation formulas by property type, location zone, size band, and use case.
              Active formulas are auto-applied during Stage 5 assessment.
            </p>
          </div>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all hover:opacity-90"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--accent-foreground)' }}
          >
            <Plus size={16} />
            New Formula
          </button>
        </div>

        {/* Save Alert */}
        {savedAlert && (
          <div
            className="flex items-center gap-2 px-4 py-3 rounded-lg"
            style={{ backgroundColor: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)' }}
          >
            <CheckCircle size={16} style={{ color: '#34d399' }} />
            <span className="text-sm font-medium" style={{ color: '#34d399' }}>
              Formula saved successfully. Active formulas will be applied in Stage 5 assessment.
            </span>
          </div>
        )}

        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Total Formulas', value: stats.total, color: 'var(--dark-text)', accent: 'var(--accent)' },
            { label: 'Active (Stage 5 Ready)', value: stats.active, color: '#34d399', accent: 'rgba(52,211,153,0.12)' },
            { label: 'Draft (Pending Review)', value: stats.draft, color: '#fbbf24', accent: 'rgba(251,191,36,0.12)' },
            { label: 'Archived', value: stats.archived, color: '#9ca3af', accent: 'rgba(156,163,175,0.12)' },
          ].map(s => (
            <div
              key={s.label}
              className="rounded-xl p-4"
              style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
            >
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{s.label}</p>
              <p className="text-3xl font-bold mt-1" style={{ color: s.color }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          {([
            { key: 'formulas', label: 'Formula Library', icon: <Calculator size={14} /> },
            { key: 'matrix', label: 'Coverage Matrix', icon: <Layers size={14} /> },
            { key: 'guide', label: 'Policy Guide', icon: <BookOpen size={14} /> },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors"
              style={{
                borderColor: activeTab === tab.key ? 'var(--accent)' : 'transparent',
                color: activeTab === tab.key ? 'var(--accent)' : 'var(--dark-muted)',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── FORMULA LIBRARY TAB ── */}
        {activeTab === 'formulas' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-48">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                <input
                  style={{
                    backgroundColor: 'var(--dark-elevated)',
                    border: '1px solid var(--dark-border)',
                    color: 'var(--dark-text)',
                    borderRadius: '6px',
                    padding: '7px 10px 7px 32px',
                    fontSize: '13px',
                    width: '100%',
                    outline: 'none',
                  }}
                  placeholder="Search formulas..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <Filter size={13} style={{ color: 'var(--dark-muted)' }} />
                <select style={selectStyle} value={filterType} onChange={e => setFilterType(e.target.value as PropertyType | 'All')}>
                  <option value="All">All Types</option>
                  {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <select style={selectStyle} value={filterZone} onChange={e => setFilterZone(e.target.value as LocationZone | 'All')}>
                  <option value="All">All Zones</option>
                  {LOCATION_ZONES.map(z => <option key={z} value={z}>{z.split('–')[0].trim()}</option>)}
                </select>
                <select style={selectStyle} value={filterStatus} onChange={e => setFilterStatus(e.target.value as FormulaStatus | 'All')}>
                  <option value="All">All Status</option>
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            {/* Formula Cards */}
            <div className="space-y-3">
              {filtered.length === 0 ? (
                <div className="text-center py-12" style={{ color: 'var(--dark-muted)' }}>
                  <Calculator size={32} className="mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No formulas match your filters.</p>
                </div>
              ) : (
                filtered.map(f => (
                  <FormulaCard
                    key={f.id}
                    formula={f}
                    onEdit={() => openEdit(f)}
                    onDuplicate={() => handleDuplicate(f)}
                    onToggleStatus={() => handleToggleStatus(f.id)}
                    onDelete={() => handleDelete(f.id)}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* ── COVERAGE MATRIX TAB ── */}
        {activeTab === 'matrix' && (
          <div className="space-y-4">
            <div
              className="flex items-center gap-2 px-4 py-3 rounded-lg"
              style={{ backgroundColor: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.15)' }}
            >
              <Info size={14} style={{ color: '#60a5fa' }} />
              <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
                Matrix shows active formulas only. Cells with no formula will fall back to the default LRA base rate during Stage 5 assessment.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid var(--dark-border)' }}>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ backgroundColor: 'var(--dark-elevated)' }}>
                    <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--dark-muted)', minWidth: '130px' }}>
                      Property Type
                    </th>
                    {LOCATION_ZONES.map(z => (
                      <th key={z} className="text-center px-3 py-3 font-semibold" style={{ color: 'var(--dark-muted)', minWidth: '120px' }}>
                        {z.split('–')[0].trim()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrixData.map((row, ri) => (
                    <tr
                      key={row.type}
                      style={{ borderTop: '1px solid var(--dark-border)', backgroundColor: ri % 2 === 0 ? 'var(--dark-surface)' : 'rgba(255,255,255,0.01)' }}
                    >
                      <td className="px-4 py-3 font-medium" style={{ color: 'var(--dark-text)' }}>
                        <div className="flex items-center gap-2">
                          <span style={{ color: 'var(--accent)' }}>{propertyTypeIcon[row.type]}</span>
                          {row.type}
                        </div>
                      </td>
                      {row.zones.map(cell => (
                        <td key={cell.zone} className="px-3 py-3 text-center">
                          {cell.formula ? (
                            <div>
                              <div
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                                style={{ backgroundColor: 'rgba(52,211,153,0.12)', color: '#34d399' }}
                              >
                                <CheckCircle size={10} />
                                ${cell.formula.baseRatePerSqm}/m²
                              </div>
                              <p className="text-xs mt-1 truncate max-w-24 mx-auto" style={{ color: 'var(--dark-muted)' }}>
                                {cell.formula.name.split(' ').slice(0, 2).join(' ')}
                              </p>
                            </div>
                          ) : (
                            <div
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                              style={{ backgroundColor: 'rgba(248,113,113,0.08)', color: '#f87171' }}
                            >
                              <AlertTriangle size={10} />
                              No formula
                            </div>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--dark-muted)' }}>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(52,211,153,0.4)' }} />
                Active formula configured
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgba(248,113,113,0.4)' }} />
                No formula — default rate applies
              </span>
            </div>
          </div>
        )}

        {/* ── POLICY GUIDE TAB ── */}
        {activeTab === 'guide' && (
          <div className="space-y-4">
            {[
              {
                title: 'How Valuation Formulas Work',
                color: 'var(--accent)',
                bg: 'rgba(212,160,23,0.08)',
                border: 'rgba(212,160,23,0.2)',
                content: [
                  'Each formula defines a tax computation rule for a specific combination of property type, location zone, size band, and use case.',
                  'The formula: Tax = CLAMP(Area × BaseRate × LocationMultiplier × UseMultiplier × (1 − Depreciation%), MinTax, MaxTax)',
                  'During Stage 5 (Property Assessment), the system automatically selects the best-matching active formula for each property record.',
                  'If no exact match exists, the system falls back to the closest matching formula or the LRA default base rate.',
                ],
              },
              {
                title: 'Location Zone Definitions',
                color: '#60a5fa',
                bg: 'rgba(96,165,250,0.08)',
                border: 'rgba(96,165,250,0.2)',
                content: [
                  'Zone A – Central Monrovia: CBD, Waterside, Capitol Hill, Mamba Point. Highest land values.',
                  'Zone B – Greater Monrovia: Paynesville, Congo Town, Sinkor, Oldest Congo Town.',
                  'Zone C – Secondary Cities: Buchanan, Gbarnga, Voinjama, Sanniquellie, Harper.',
                  'Zone D – Rural Towns: District capitals and peri-urban settlements.',
                  'Zone E – Remote/Rural: Villages, farmland, forest concessions, remote areas.',
                ],
              },
              {
                title: 'Multiplier Guidelines',
                color: '#34d399',
                bg: 'rgba(52,211,153,0.08)',
                border: 'rgba(52,211,153,0.2)',
                content: [
                  'Location Multiplier: Reflects land value premium by zone. Zone A typically 1.8–2.2×, Zone E typically 0.4–0.7×.',
                  'Use-Case Multiplier: Commercial Operations 1.3–1.6×, Owner-Occupied Residential 0.8–1.0×, Agricultural 0.6–0.8×, Vacant/Idle 1.6–2.0× (anti-speculation).',
                  'Depreciation Rate: Applied to account for building age/condition. Typically 1–5% per year. Vacant land = 0%.',
                  'Min/Max Tax: Ensures equity — no property pays below minimum; large properties capped to prevent excessive burden.',
                ],
              },
              {
                title: 'Stage 5 Auto-Application',
                color: '#e879f9',
                bg: 'rgba(232,121,249,0.08)',
                border: 'rgba(232,121,249,0.2)',
                content: [
                  'Only formulas with status "Active" are applied during Stage 5 assessment runs.',
                  'Formula matching priority: Exact match (all 4 dimensions) → Type + Zone match → Type match → Default.',
                  'All formula applications are logged with formula ID, version, and admin who last updated it for full audit trail.',
                  'Draft formulas can be tested using the Live Preview in the formula editor before activation.',
                ],
              },
            ].map(section => (
              <div
                key={section.title}
                className="rounded-xl p-5"
                style={{ backgroundColor: section.bg, border: `1px solid ${section.border}` }}
              >
                <h3 className="font-bold text-sm mb-3" style={{ color: section.color }}>{section.title}</h3>
                <ul className="space-y-2">
                  {section.content.map((line, i) => (
                    <li key={i} className="flex gap-2 text-sm" style={{ color: 'var(--dark-muted)' }}>
                      <span className="flex-shrink-0 mt-0.5" style={{ color: section.color }}>›</span>
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && modalFormula && (
        <FormulaModal
          formula={modalFormula}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </AppLayout>
  );
}
